import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

export default function SustainabilityBusiness() {
  const { t } = useLanguage();

  const businessCards = [
    {
      icon: 'ri-box-3-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      number: '01',
      title: t('低碳產品線開發', 'Low-Carbon Product Line Development', '低炭素製品ライン開発'),
      desc: t(
        '協助品牌端以再生料為基礎，開發可量產、可認證、可對外溝通的低碳產品線。',
        'Help brands develop scalable, certifiable, and communicable low-carbon product lines based on recycled materials.',
        'ブランドが再生材料を基盤とした量産可能・認証可能・対外コミュニケーション可能な低炭素製品ラインを開発できるよう支援します。'
      ),
      image: 'https://readdy.ai/api/search-image?query=low%20carbon%20sustainable%20product%20development%20recycled%20plastic%20packaging%20green%20label%20eco%20friendly%20brand%20product%20line%20clean%20white%20background%20professional%20product%20photography%20green%20accent%20high%20resolution&width=480&height=240&seq=biz-product-v1&orientation=landscape',
    },
    {
      icon: 'ri-flask-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      number: '02',
      title: t('再生料導入與驗證', 'Recycled Material Implementation & Verification', '再生材料導入・検証'),
      desc: t(
        '從原料評估、配方設計、試產驗證到量產導入，提供完整的再生料技術支援服務。',
        'From raw material assessment, formulation design, pilot production verification to full-scale implementation, providing comprehensive recycled material technical support services.',
        '原料評価・配合設計・試験生産検証・量産導入まで、包括的な再生材料技術サポートサービスを提供します。'
      ),
      image: 'https://readdy.ai/api/search-image?query=recycled%20material%20testing%20verification%20laboratory%20plastic%20pellets%20quality%20control%20industrial%20research%20professional%20photography%20blue%20accent%20lighting%20clean%20modern%20lab%20environment%20high%20resolution&width=480&height=240&seq=biz-verify-v1&orientation=landscape',
    },
    {
      icon: 'ri-database-2-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      number: '03',
      title: t('回收履歷與資料化管理', 'Recycling Records & Data Management', '回収履歴・データ管理'),
      desc: t(
        '建立再生料來源追蹤、使用比例記錄與碳效益數據，支援 ESG 報告與供應鏈透明化。',
        'Establish recycled material source tracking, usage ratio records, and carbon benefit data to support ESG reporting and supply chain transparency.',
        '再生材料の源泉追跡・使用比率記録・炭素効果データを構築し、ESGレポートとサプライチェーンの透明性をサポートします。'
      ),
      image: 'https://readdy.ai/api/search-image?query=data%20management%20recycling%20traceability%20supply%20chain%20transparency%20digital%20dashboard%20ESG%20reporting%20circular%20economy%20data%20visualization%20professional%20photography%20green%20blue%20accent%20clean%20modern%20environment&width=480&height=240&seq=biz-data-v1&orientation=landscape',
    },
    {
      icon: 'ri-megaphone-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      number: '04',
      title: t('ESG 與碳效益溝通素材', 'ESG & Carbon Benefit Communication Materials', 'ESG・炭素効果コミュニケーション素材'),
      desc: t(
        '協助整理再生料導入成果，提供可用於 ESG 報告、品牌溝通與投資人說明的碳效益素材。',
        'Help compile recycled material adoption results, providing carbon benefit materials usable for ESG reports, brand communication, and investor presentations.',
        '再生材料導入成果の整理を支援し、ESGレポート・ブランドコミュニケーション・投資家説明に使用可能な炭素効果素材を提供します。'
      ),
      image: 'https://readdy.ai/api/search-image?query=ESG%20sustainability%20report%20carbon%20footprint%20communication%20material%20corporate%20presentation%20green%20technology%20professional%20photography%20clean%20modern%20office%20blue%20green%20accent%20high%20resolution&width=480&height=240&seq=biz-esg-v1&orientation=landscape',
    },
  ];

  return (
    <section className="section-padding bg-white">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-end mb-12">
          <div>
            <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-3">
              {t('永續商業模式', 'Sustainable Business Model', '持続可能なビジネスモデル')}
            </p>
            <h2
              className="font-bold text-neutral-dark mb-4"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}
            >
              {t('從材料技術到永續商業模式', 'From Material Technology to Sustainable Business Model', '材料技術から持続可能なビジネスモデルへ')}
            </h2>
          </div>
          <div>
            <p className="text-neutral-mid" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>
              {t(
                '聚量不是只提供材料，而是協助客戶把再生材料變成可商品化、可報告、可擴散的低碳解決方案。',
                'PolyQ doesn\'t just provide materials — we help clients transform recycled materials into commercializable, reportable, and scalable low-carbon solutions.',
                '聚量は材料を提供するだけでなく、クライアントが再生材料を商品化可能・報告可能・拡散可能な低炭素ソリューションへと転換できるよう支援します。'
              )}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {businessCards.map((card) => (
            <div
              key={card.number}
              className="group bg-white rounded-card border border-neutral-border overflow-hidden flex flex-col hover:-translate-y-1 transition-all duration-250"
              style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.07)' }}
            >
              <div className="w-full h-[160px] overflow-hidden flex-shrink-0">
                <img src={card.image} alt={card.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="flex flex-col flex-1 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${card.iconBg} flex-shrink-0`}>
                    <i className={`${card.icon} ${card.iconColor} text-xl`}></i>
                  </div>
                  <span className="text-[0.8125rem] font-bold text-neutral-mid tracking-widest">{card.number}</span>
                </div>
                <h3 className="font-semibold text-neutral-dark mb-2 group-hover:text-brand-green transition-colors" style={{ fontSize: '1.0625rem', lineHeight: '1.5' }}>{card.title}</h3>
                <p className="text-neutral-mid text-[1rem] leading-relaxed flex-1">{card.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-brand-green text-brand-green text-[1.0625rem] font-semibold hover:bg-brand-green hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-calendar-schedule-line"></i>
            {t('了解如何導入永續商業模式', 'Learn How to Implement Sustainable Business Models', '持続可能なビジネスモデルの導入方法を知る')}
          </Link>
        </div>
      </div>
    </section>
  );
}
